import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import { $i } from '@silae/composables/icons';
import { defaultLayoutComponents, defaultLayoutProps } from '~/pages/layout';
import { ROUTE_KEYS } from '~/router';
import { useAuthorizationStore } from '~/stores';

function hasOrgIdParamDefined(context: Record<string, unknown>) {
	return !!context?.orgId;
}

export const OrgUsersRoute: RouteRecordRaw = {
	path: 'users',
	name: ROUTE_KEYS.ORGANIZATION_USERS,
	meta: {
		label: 'users.page-title',
		auth: true,
		isNavEntry: true,
		icon: () => $i('users'),
		isAuthorized: params => computed(() => hasOrgIdParamDefined(params))
	},
	component: () => import('./components/routes/users/users.page.vue')
};

export const OrgInvitationsRoute: RouteRecordRaw = {
	path: 'invitations',
	name: ROUTE_KEYS.ORGANIZATION_INVITATIONS,
	meta: {
		label: 'invitations.page-title',
		auth: true,
		isNavEntry: true,
		icon: () => $i('envelope'),
		isAuthorized: params => computed(() => hasOrgIdParamDefined(params))
	},
	component: () => import('./components/routes/invitations/invitations.page.vue')
};

export const OrgExpenseTrailRoute: RouteRecordRaw = {
	path: 'expense-trail',
	name: ROUTE_KEYS.ORGANIZATION_EXPENSE_TRAIL,
	meta: {
		label: 'expense-trail.page-title',
		auth: true,
		isNavEntry: true,
		icon: () => $i('list-timeline'),
		isAuthorized: params => computed(() => hasOrgIdParamDefined(params))
	},
	component: () => import('./components/routes/expense-trail/expense-trail.page.vue')
};

export const OrgActivity: RouteRecordRaw = {
	path: 'activity',
	name: ROUTE_KEYS.ORGANIZATION_ACTIVITY,
	meta: {
		label: 'activity.page-title',
		auth: true,
		isNavEntry: true,
		icon: () => $i('chart-line-up'),
		isAuthorized: params => {
			const { hasBilling } = storeToRefs(useAuthorizationStore());
			return computed(() => hasOrgIdParamDefined(params) && hasBilling.value);
		}
	},
	component: () => import('./components/routes/activity/activity.page.vue')
};

const OrganizationRoute: RouteRecordRaw = {
	path: '/organizations/:orgId',
	name: ROUTE_KEYS.ORGANIZATION,
	meta: {
		auth: true,
		isAuthorized: params => computed(() => hasOrgIdParamDefined(params))
	},
	props: {
		...defaultLayoutProps
	},
	components: {
		default: () => import('./organization.page.vue'),
		...defaultLayoutComponents
	},
	children: [OrgUsersRoute, OrgInvitationsRoute, OrgExpenseTrailRoute, OrgActivity],
	redirect: route => ({
		...OrgUsersRoute,
		params: {
			orgId: route?.params?.orgId
		}
	})
};

export default OrganizationRoute;
